import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";

import Popover from "@material-ui/core/Popover";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Link from "@material-ui/core/Link";

import TicketListItem from "../TicketListItem";
import useTickets from "../../hooks/useTickets";
import defaultAlertSound from "../../assets/sound.mp3";
import { getSoundUrl, playCompanyAlertSound } from "../../utils/soundHelper";
import { parseHeaderConfig } from "../../utils/headerConfig";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { resolveContactAvatar } from "../../utils/avatar";
import toastError from "../../errors/toastError";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import Favicon from "react-favicon";
import defaultLogoFavicon from "../../assets/favicon.png";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import IconButtonWithTooltip from "../ui/IconButtonWithTooltip";
import StandardModal from "../ui/StandardModal";
import { Bell } from "lucide-react";
import api from "../../services/api";
import { getTicketById } from "../../services/ticketsApi";
import { getTicketsRealtimeFallbackIntervalMs } from "../../config";

const useStyles = makeStyles((theme) => ({
  tabContainer: {
    overflowY: "auto",
    maxHeight: 350,
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    ...theme.scrollbarStyles,
    padding: theme.spacing(0, 0.25),
  },
  popoverPaper: {
    width: "100%",
    maxWidth: 350,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      maxWidth: 270,
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  noShadow: {
    boxShadow: "none !important",
  },
  popoverHeader: {
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.tabHeaderDark
        : theme.palette.tabHeaderLight,
    color: theme.palette.type === "dark" ? "#fff" : "#000",
    padding: theme.spacing(0.5, 1.5),
    fontSize: "14px",
    fontWeight: "bold",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.15)"
        : "rgba(0,0,0,0.1)"
    }`,
  },
  emptyList: {
    padding: theme.spacing(1.5),
    textAlign: "center",
    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.7)"
        : "rgba(0,0,0,0.6)",
    fontSize: "13px",
  },
  notificationContainer: {
    width: "100%",
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5, 0.5),
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.15)"
        : "rgba(0,0,0,0.08)"
    }`,
  },
  filterButton: {
    textTransform: "none",
    minWidth: "auto",
    borderRadius: 999,
    padding: theme.spacing(0.5, 1.25),
    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.85)"
        : "#4b5563",
  },
  filterButtonActive: {
    fontWeight: "bold",
  },
  todoNotificationItem: {
    margin: theme.spacing(0.5, 0.75),
    borderRadius: 12,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    alignItems: "flex-start",
    background: theme.palette.type === "dark" ? "#1f2937" : "#ffffff",
  },
  todoNotificationUnread: {
    background:
      theme.palette.type === "dark"
        ? "rgba(59,130,246,0.14)"
        : "rgba(59,130,246,0.08)",
    borderLeft: "3px solid #3b82f6",
  },
  todoAvatar: {
    backgroundColor: "rgba(59,130,246,0.12)",
    color: "#2563eb",
    fontWeight: 700,
  },
  todoPrimary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  todoTitle: {
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#fff" : "#111827",
  },
  todoMeta: {
    marginTop: theme.spacing(0.5),
    fontSize: "12px",
    lineHeight: 1.45,
  },
  unreadBadge: {
    height: 22,
    fontWeight: 700,
    color: "#2563eb",
    backgroundColor: "rgba(59,130,246,0.12)",
  },
  notificationTicket: {
    width: "100%",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  ticketItemContainer: {
    margin: theme.spacing(0.25, 0),
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(0.25, 0),
    },
  },
}));

const getPlainNotificationText = (value) => {
  if (!value) {
    return "";
  }

  const normalized = String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n");

  try {
    const doc = new DOMParser().parseFromString(normalized, "text/html");
    return (doc.body.textContent || "")
      .replace(/\s*\n\s*/g, "\n")
      .replace(/[ \t]+/g, " ")
      .trim();
  } catch {
    return normalized
      .replace(/<[^>]*>/g, "")
      .replace(/\s*\n\s*/g, "\n")
      .replace(/[ \t]+/g, " ")
      .trim();
  }
};

const normalizeFromMe = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "sim"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "nao", "não", ""].includes(normalized)) {
      return false;
    }
  }

  return Boolean(value);
};

const toNumberOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getTicketIdentifierFromLocation = (location) => {
  const pathname = location?.pathname || "";
  const hash = typeof window !== "undefined" ? window.location?.hash || "" : "";
  const source = `${pathname} ${hash}`;
  const match = source.match(/\/tickets\/([^/?#]+)/i);
  return match ? match[1] : "";
};

const canUseDesktopNotifications = () => {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    Notification.permission === "granted"
  );
};

const isPendingTicketNotification = (ticket) => ticket?.status === "pending";
const unreadTicketStatusFilter = JSON.stringify(["pending", "open"]);

const NotificationsPopOver = React.memo(({ volume, icon: Icon = Bell }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { profile, queues } = user;

  const currentTicketPathRef = useRef(
    getTicketIdentifierFromLocation(history.location)
  );
  const ticketIdRef = useRef(
    toNumberOrNull(getTicketIdentifierFromLocation(history.location))
  );
  const anchorEl = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [ticketNotifications, setTicketNotifications] = useState([]);
  const [chatNotifications, setChatNotifications] = useState([]);
  const [todoNotifications, setTodoNotifications] = useState([]);
  const [supportNotifications, setSupportNotifications] = useState([]);
  const [scheduleReminder, setScheduleReminder] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const { get: getSetting } = useCompanySettings();
  const { setTabOpen } = useContext(TicketsContext);

  const [showTicketWithoutQueue, setShowTicketWithoutQueue] = useState(false);
  const [showNotificationPending, setShowNotificationPending] = useState(true);
  const [showGroupNotification, setShowGroupNotification] = useState(false);

  const [desktopNotifications, setDesktopNotifications] = useState([]);
  const fallbackIntervalMs = useMemo(
    () => getTicketsRealtimeFallbackIntervalMs(),
    []
  );

  const { tickets } = useTickets({
    withUnreadMessages: true,
    pageNumber: 1,
    statusFilter: unreadTicketStatusFilter,
    sortTickets: "DESC",
  });

  const mergeTicketNotifications = useCallback((nextTickets) => {
    const nextMap = new Map();

    (nextTickets || []).forEach((ticket) => {
      if (ticket?.id) {
        nextMap.set(ticket.id, ticket);
      }
    });

    setTicketNotifications((prevState) => {
      prevState.forEach((ticket) => {
        if (
          ticket?.id &&
          Number(ticket.unreadMessages || 0) > 0 &&
          !["closed", "lgpd", "nps"].includes(ticket.status) &&
          !nextMap.has(ticket.id)
        ) {
          nextMap.set(ticket.id, ticket);
        }
      });

      return Array.from(nextMap.values())
        .filter((ticket) => {
          if (!ticket?.id) return false;
          if (["closed", "lgpd", "nps"].includes(ticket.status)) return false;
          return Number(ticket.unreadMessages || 0) > 0;
        })
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    });
  }, []);

  const normalizedVolume = useMemo(() => {
    const vol = typeof volume === "number" ? volume : Number(volume) || 1;
    return Math.max(0, Math.min(1, vol));
  }, [volume]);

  const soundAlertRef = useRef();
  const fallbackAudioRef = useRef(null);
  const handleNotificationsRef = useRef(null);
  const previousTicketUnreadRef = useRef(null);
  const previousTicketLatestTsRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  const [alertSoundUrl, setAlertSoundUrl] = useState(() => {
    try {
      const stored = localStorage.getItem("headerConfig");
      const cfg = parseHeaderConfig(stored);
      return getSoundUrl(cfg.alertSound);
    } catch (_) {
      return defaultAlertSound;
    }
  });

  useEffect(() => {
    const handleHeaderConfigUpdate = (event) => {
      const headerConfig = parseHeaderConfig(event.detail);
      const newUrl = getSoundUrl(headerConfig.alertSound);
      setAlertSoundUrl(newUrl);
      if (fallbackAudioRef.current) {
        fallbackAudioRef.current.pause();
        fallbackAudioRef.current = new Audio(newUrl);
        fallbackAudioRef.current.preload = "auto";
      }
    };

    window.addEventListener("header-config-updated", handleHeaderConfigUpdate);
    return () => window.removeEventListener("header-config-updated", handleHeaderConfigUpdate);
  }, []);

  const unlockAudioPlayback = useCallback(() => {
    if (audioUnlockedRef.current) return;

    try {
      if (!fallbackAudioRef.current) {
        fallbackAudioRef.current = new Audio(alertSoundUrl);
        fallbackAudioRef.current.preload = "auto";
      }

      const audio = fallbackAudioRef.current;
      const previousMuted = audio.muted;

      audio.muted = true;
      audio.volume = 0;
      audio.currentTime = 0;

      const restore = () => {
        audio.muted = previousMuted;
        audio.volume = normalizedVolume;
      };

      const markUnlocked = () => {
        audio.pause();
        audio.currentTime = 0;
        restore();
        audioUnlockedRef.current = true;
      };

      const promise = audio.play();
      if (promise && typeof promise.then === "function") {
        promise.then(markUnlocked).catch(restore);
      } else {
        markUnlocked();
      }
    } catch (_) {}
  }, [normalizedVolume, alertSoundUrl]);

  useEffect(() => {
    const tryUnlock = () => {
      unlockAudioPlayback();
    };

    window.addEventListener("pointerdown", tryUnlock);
    window.addEventListener("touchstart", tryUnlock);
    window.addEventListener("keydown", tryUnlock);
    window.addEventListener("click", tryUnlock);

    return () => {
      window.removeEventListener("pointerdown", tryUnlock);
      window.removeEventListener("touchstart", tryUnlock);
      window.removeEventListener("keydown", tryUnlock);
      window.removeEventListener("click", tryUnlock);
    };
  }, [unlockAudioPlayback]);

  useEffect(() => {
    soundAlertRef.current = (contactSoundId) => {
      try {
        playCompanyAlertSound(normalizedVolume, contactSoundId);
      } catch (_) {}
    };
  }, [normalizedVolume]);

  const historyRef = useRef(history);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    const updateActiveTicket = (location) => {
      const activeIdentifier = getTicketIdentifierFromLocation(
        location || history.location
      );
      currentTicketPathRef.current = activeIdentifier;
      ticketIdRef.current = toNumberOrNull(activeIdentifier);
    };

    updateActiveTicket(history.location);

    const unlisten = history.listen((location) => {
      updateActiveTicket(location);
    });

    const handleHashChange = () => {
      updateActiveTicket(history.location);
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      if (typeof unlisten === "function") unlisten();
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [history]);

  const isTicketCurrentlyVisible = useCallback(
    (ticket, messageTicketId) => {
      if (document.visibilityState !== "visible") {
        return false;
      }
      const activeParam = currentTicketPathRef.current;
      if (!activeParam) {
        return false;
      }

      const activeParamLower = String(activeParam).toLowerCase();
      const ticketIdStr = ticket?.id != null ? String(ticket.id) : null;
      const ticketUuidStr = ticket?.uuid ? String(ticket.uuid).toLowerCase() : null;
      const msgTicketIdStr =
        messageTicketId != null ? String(messageTicketId) : null;

      return (
        (ticketIdStr && ticketIdStr === activeParam) ||
        (ticketUuidStr && ticketUuidStr === activeParamLower) ||
        (msgTicketIdStr && msgTicketIdStr === activeParam)
      );
    },
    []
  );

  useEffect(() => {
    if (!("Notification" in window)) {
    } else {
      Notification.requestPermission();
    }
  }, []);

  // Fallback geral: força revalidação periódica dos tickets/notificações
  // para não depender exclusivamente do socket em ambientes instáveis.
  useEffect(() => {
    const interval = setInterval(() => {
      if (socket && socket.connected) {
        return;
      }
      window.dispatchEvent(new Event("tickets:changed"));
    }, fallbackIntervalMs);

    return () => clearInterval(interval);
  }, [fallbackIntervalMs, socket]);

  useEffect(() => {
    if (!Array.isArray(tickets)) return;

    const filteredTickets = tickets.filter((ticket) => {
      if (ticket.isGroup) {
        return (
          showGroupNotification && ticket.whatsapp?.groupAsTicket === "enabled"
        );
      }
      return true;
    });

    mergeTicketNotifications(filteredTickets);
  }, [tickets, showGroupNotification, mergeTicketNotifications]);

  useEffect(() => {
    const pendingTickets = (ticketNotifications || []).filter(
      isPendingTicketNotification
    );
    const unreadTotal = pendingTickets.reduce(
      (sum, ticket) => sum + Number(ticket?.unreadMessages || 0),
      0
    );
    const latestTs = pendingTickets.reduce((maxTs, ticket) => {
      const ts = new Date(ticket?.updatedAt || ticket?.lastMessageAt || 0).getTime();
      if (!Number.isFinite(ts)) return maxTs;
      return Math.max(maxTs, ts);
    }, 0);

    if (previousTicketUnreadRef.current === null) {
      previousTicketUnreadRef.current = unreadTotal;
      previousTicketLatestTsRef.current = latestTs;
      return;
    }

    const hasNewUnread = unreadTotal > previousTicketUnreadRef.current;
    const hasRecentTicketUpdate =
      Number(latestTs || 0) > Number(previousTicketLatestTsRef.current || 0);

    if (hasNewUnread || (hasRecentTicketUpdate && unreadTotal > 0)) {
      if (soundAlertRef.current) {
        soundAlertRef.current();
      }

      if (document.visibilityState !== "visible" && canUseDesktopNotifications()) {
        try {
          new Notification("Nova mensagem recebida", {
            body: "Você possui novas mensagens pendentes.",
            tag: "ticket-unread-fallback",
            renotify: true,
          });
        } catch (_) {}
      }
    }

    previousTicketUnreadRef.current = unreadTotal;
    previousTicketLatestTsRef.current = latestTs;
  }, [ticketNotifications]);

  useEffect(() => {
    const loadTodoNotifications = async () => {
      try {
        const { data } = await api.get("/todo-task-notifications");
        setTodoNotifications(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    };

    if (user?.id) {
      loadTodoNotifications();
    }
  }, [user?.id]);

  useEffect(() => {
    const loadSupportNotifications = async () => {
      try {
        const { data } = await api.get("/support-ticket-notifications");
        setSupportNotifications(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    };

    if (user?.id) {
      loadSupportNotifications();
    }
  }, [user?.id]);

  useEffect(() => {
    const loadChatNotifications = async () => {
      try {
        const { data } = await api.get("/internal-chats");
        if (Array.isArray(data?.records)) {
          const unreadChats = data.records.filter((chat) => {
            const userChat = chat.users?.find(
              (u) => Number(u.userId) === Number(user?.id)
            );
            return Number(userChat?.unreads || 0) > 0;
          });
          setChatNotifications(
            unreadChats.map((chat) => ({
              ...chat,
              type: "internalChat",
            }))
          );
        }
      } catch (err) {
        console.error("Erro ao carregar notificações de chat interno:", err);
      }
    };

    if (user?.id) {
      loadChatNotifications();
    }
  }, [user?.id]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting({
          column: "showNotificationPending",
        });

        // A API retorna um array: [{showNotificationPending: <value>}]
        // Só desabilita se o DB retornar EXPLICITAMENTE false.
        // null/undefined/true/"true"/"enabled" mantêm habilitado (padrão ativo).
        const rawValue = Array.isArray(setting)
          ? setting[0]?.showNotificationPending
          : setting?.showNotificationPending;

        const isDisabled = rawValue === false || rawValue === "false" || rawValue === "disabled";
        setShowNotificationPending(!isDisabled);

        if (user.allTicket === "enabled") {
          setShowTicketWithoutQueue(true);
        }
        if (user.allowGroup === true) {
          setShowGroupNotification(true);
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchSettings();
  }, [getSetting, user]);

  const closeDesktopNotificationByTag = useCallback((tag) => {
    setDesktopNotifications((prevState) => {
      const notificationIndex = prevState.findIndex(
        (notification) => String(notification.tag) === String(tag)
      );

      if (notificationIndex === -1) {
        return prevState;
      }

      const updated = [...prevState];
      updated[notificationIndex].close();
      updated.splice(notificationIndex, 1);
      return updated;
    });
  }, []);

  const openTicketFromNotification = useCallback(
    (ticket) => {
      if (!ticket?.id) {
        return;
      }

      setIsOpen(false);
      setTicketNotifications((prevState) =>
        prevState.filter((item) => item.id !== ticket.id)
      );
      closeDesktopNotificationByTag(ticket.id);

      setTimeout(() => {
        if (setTabOpen) {
          setTabOpen(ticket.status);
        }
      }, 10);

      historyRef.current.push(`/tickets/${ticket.uuid || ticket.id}`);
    },
    [setTabOpen, closeDesktopNotificationByTag]
  );

  const openTodoFromNotification = useCallback(
    (notificationData) => {
      if (!notificationData?.id) {
        return;
      }

      setIsOpen(false);
      api
        .put(`/todo-task-notifications/${notificationData.id}/read`)
        .catch(toastError);
      setTodoNotifications((prevState) =>
        prevState.filter((item) => item.id !== notificationData.id)
      );
      closeDesktopNotificationByTag(notificationData.id);
      historyRef.current.push("/todolist");
    },
    [closeDesktopNotificationByTag]
  );

  const openSupportFromNotification = useCallback(
    (notificationData) => {
      if (!notificationData?.id) {
        return;
      }

      setIsOpen(false);
      api
        .put(`/support-ticket-notifications/${notificationData.id}/read`)
        .catch(toastError);
      setSupportNotifications((prevState) =>
        prevState.filter((item) => item.id !== notificationData.id)
      );
      closeDesktopNotificationByTag(`support-${notificationData.id}`);

      if (notificationData.ticketId) {
        historyRef.current.push(`/support-tickets/${notificationData.ticketId}`);
      }
    },
    [closeDesktopNotificationByTag]
  );

  const openChatFromNotification = useCallback(
    async (chat) => {
      if (!chat?.id) return;

      setIsOpen(false);
      setChatNotifications((prev) => prev.filter((c) => c.id !== chat.id));
      closeDesktopNotificationByTag(`chat-${chat.id}`);

      try {
        await api.post(`/internal-chats/${chat.id}/read`, {
          userId: user?.id,
        });
      } catch (err) {
        console.error("Erro ao marcar chat como lido:", err);
      }

      historyRef.current.push(`/chats/${chat.uuid || chat.id}`);
    },
    [user?.id, closeDesktopNotificationByTag]
  );

  const handleNotifications = useCallback(
    (data) => {
      const message = data?.message;
      const ticket = data?.ticket || data?.message?.ticket;

      if (!ticket || !ticket.id || !message) {
        console.warn("[Notifications] handleNotifications ignorado: ticket ou message ausente.", {
          hasTicket: Boolean(ticket),
          hasTicketId: Boolean(ticket?.id),
          hasMessage: Boolean(message),
        });
        return;
      }

      const contact = data?.contact || ticket?.contact || {};
      const plainMessageBody = getPlainNotificationText(message?.body);

      if (canUseDesktopNotifications()) {
        const options = {
          body: `${plainMessageBody || "[mensagem]"} - ${format(
            new Date(),
            "HH:mm"
          )}`,
          icon: resolveContactAvatar(contact),
          tag: String(ticket.id),
          renotify: true,
        };
        const contactName = contact?.name || "";
        const notification = new Notification(
          `${i18n.t("tickets.notification.message")} ${contactName}`.trim(),
          options
        );

        notification.onclick = (e) => {
          e.preventDefault();
          window.focus();
          openTicketFromNotification(ticket);
        };

        setDesktopNotifications((prevState) => {
          const notificationIndex = prevState.findIndex(
            (n) => n.tag === notification.tag
          );
          if (notificationIndex !== -1) {
            prevState[notificationIndex] = notification;
            return [...prevState];
          }
          return [notification, ...prevState];
        });
      } else {
        console.info("[Notifications] Desktop notification ignorada: permissao nao concedida.", {
          permission:
            typeof window !== "undefined" && "Notification" in window
              ? Notification.permission
              : "unsupported",
          ticketId: ticket?.id,
          messageId: message?.id,
        });
      }

      if (soundAlertRef.current) {
        soundAlertRef.current(contact?.notificationSound);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openTicketFromNotification, normalizedVolume]
  );

  useEffect(() => {
    handleNotificationsRef.current = handleNotifications;
  }, [handleNotifications]);

  const handleTodoDesktopNotification = useCallback(
    (notificationData) => {
      if (canUseDesktopNotifications()) {
        const options = {
          body: `${notificationData.actorName}: ${notificationData.title}`,
          tag: notificationData.id,
          renotify: true,
        };

        const notification = new Notification("Nova atualização em tarefa", options);

        notification.onclick = (e) => {
          e.preventDefault();
          window.focus();
          openTodoFromNotification(notificationData);
        };

        setDesktopNotifications((prevState) => {
          const notificationIndex = prevState.findIndex(
            (n) => n.tag === notification.tag
          );
          if (notificationIndex !== -1) {
            prevState[notificationIndex] = notification;
            return [...prevState];
          }
          return [notification, ...prevState];
        });
      } else {
        console.info("[Notifications] Todo desktop notification ignorada: permissao nao concedida.", {
          permission:
            typeof window !== "undefined" && "Notification" in window
              ? Notification.permission
              : "unsupported",
          notificationId: notificationData?.id,
        });
      }

      if (soundAlertRef.current) {
        soundAlertRef.current();
      }
    },
    [openTodoFromNotification]
  );

  const handleSupportDesktopNotification = useCallback(
    (notificationData) => {
      if (canUseDesktopNotifications()) {
        const options = {
          body: notificationData.message || notificationData.title || "Você recebeu uma atualização de suporte.",
          tag: `support-${notificationData.id}`,
          renotify: true,
        };

        const notification = new Notification("Atualização de suporte", options);

        notification.onclick = (e) => {
          e.preventDefault();
          window.focus();
          openSupportFromNotification(notificationData);
        };

        setDesktopNotifications((prevState) => [notification, ...prevState]);
      } else {
        console.info("[Notifications] Support desktop notification ignorada: permissao nao concedida.", {
          permission:
            typeof window !== "undefined" && "Notification" in window
              ? Notification.permission
              : "unsupported",
          notificationId: notificationData?.id,
        });
      }

      if (soundAlertRef.current) {
        soundAlertRef.current();
      }
    },
    [openSupportFromNotification]
  );

  const handleScheduleReminderNotification = useCallback(
    (notificationData) => {
      if (!notificationData?.id) {
        return;
      }

      if (document.visibilityState === "visible") {
        setScheduleReminder(notificationData);
        if (soundAlertRef.current) {
          soundAlertRef.current();
        }
        return;
      }

      if (canUseDesktopNotifications()) {
        const notification = new Notification(
          notificationData.title || "Agendamento",
          {
            body:
              notificationData.message ||
              "Você possui um agendamento interno pendente.",
            tag: `schedule-${notificationData.id}`,
            renotify: true,
          }
        );

        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          history.push("/schedules");
        };

        setDesktopNotifications((prevState) => [notification, ...prevState]);
      }

      if (soundAlertRef.current) {
        soundAlertRef.current();
      }
    },
    [history]
  );

  useEffect(() => {
    const companyId = user.companyId;
    if (user.id) {
      const onConnectNotificationsPopover = () => {
        socket.emit("joinNotification");
      };

      const onCompanyTicketNotificationsPopover = (data) => {
        if (data.action === "create" && data.ticket) {
          const ticket = data.ticket;
          const ticketUserId = toNumberOrNull(ticket?.userId);
          const ticketQueueId = toNumberOrNull(ticket?.queueId);
          const currentUserId = toNumberOrNull(user?.id);
          const validStatus = ["pending", "open"].includes(ticket.status);
          const isMyOwnTicket = ticketUserId && ticketUserId === currentUserId;
          const belongsToQueue =
            user?.queues?.some(
              (queue) => toNumberOrNull(queue?.id) === ticketQueueId
            ) ||
            (!ticketQueueId &&
              (showTicketWithoutQueue === true ||
                ["pending", "open"].includes(ticket.status)));

          if (
            validStatus &&
            !isMyOwnTicket &&
            belongsToQueue &&
            isPendingTicketNotification(ticket) &&
            showNotificationPending === true
          ) {
            setTicketNotifications((prevState) => {
              const ticketIndex = prevState.findIndex(
                (t) => t.id === ticket.id
              );
              if (ticketIndex !== -1) {
                const updatedTickets = [...prevState];
                updatedTickets[ticketIndex] = ticket;
                return updatedTickets;
              }
              return [ticket, ...prevState];
            });

            const isSameVisibleTicket = isTicketCurrentlyVisible(ticket);

            if (!isSameVisibleTicket) {
              if (soundAlertRef.current) {
                soundAlertRef.current();
              }

              if (
                document.visibilityState !== "visible" &&
                canUseDesktopNotifications()
              ) {
                try {
                  new Notification("Novo ticket recebido", {
                    body: `Ticket #${ticket.id}${
                      ticket.contact?.name
                        ? ` - ${ticket.contact.name}`
                        : ""
                    }`,
                    tag: `ticket-new-${ticket.id}`,
                    renotify: true,
                  });
                } catch (_) {}
              }
            }
          }
          return;
        }

        if (data.action === "update" && data.ticket) {
          const shouldRemove =
            data.ticket.unreadMessages === 0 ||
            ["closed", "lgpd", "nps"].includes(data.ticket.status);

          if (shouldRemove) {
            setTicketNotifications((prevState) =>
              prevState.filter((t) => t.id !== data.ticket.id)
            );

            setDesktopNotifications((prevState) => {
              const notificationIndex = prevState.findIndex(
                (n) => n.tag === String(data.ticket.id)
              );
              if (notificationIndex !== -1) {
                prevState[notificationIndex].close();
                prevState.splice(notificationIndex, 1);
                return [...prevState];
              }
              return prevState;
            });
          } else {
            setTicketNotifications((prevState) => {
              const ticketIndex = prevState.findIndex(
                (t) => t.id === data.ticket.id
              );
              if (ticketIndex !== -1) {
                const updatedTickets = [...prevState];
                updatedTickets[ticketIndex] = data.ticket;
                return updatedTickets;
              }
              return [...prevState, data.ticket];
            });
          }
        }

        if (data.action === "updateUnread" && data.ticketId) {
          setTicketNotifications((prevState) =>
            prevState.filter((t) => t.id !== data.ticketId)
          );

          setDesktopNotifications((prevState) => {
            const notificationIndex = prevState.findIndex(
              (n) => n.tag === String(data.ticketId)
            );
            if (notificationIndex !== -1) {
              prevState[notificationIndex].close();
              prevState.splice(notificationIndex, 1);
              return [...prevState];
            }
            return prevState;
          });
        }

        if (data.action === "delete") {
          setTicketNotifications((prevState) => {
            const ticketIndex = prevState.findIndex(
              (t) => t.id === data.ticketId
            );
            if (ticketIndex !== -1) {
              const updated = [...prevState];
              updated.splice(ticketIndex, 1);
              return updated;
            }
            return prevState;
          });

          setDesktopNotifications((prevState) => {
            const notificationIndex = prevState.findIndex(
              (n) => n.tag === String(data.ticketId)
            );
            if (notificationIndex !== -1) {
              prevState[notificationIndex].close();
              const updated = [...prevState];
              updated.splice(notificationIndex, 1);
              return updated;
            }
            return prevState;
          });
        }
      };

      const onCompanyAppMessageNotificationsPopover = async (data) => {
        let payloadTicket = data?.ticket || data?.message?.ticket || null;

        if (!payloadTicket) {
          const ticketIdFromEvent = toNumberOrNull(
            data?.message?.ticketId || data?.ticketId
          );
          if (ticketIdFromEvent) {
            try {
              const { data: fetchedTicket } = await getTicketById(
                ticketIdFromEvent
              );
              payloadTicket = fetchedTicket || null;
            } catch (_) {}
          }
        }

        if (!payloadTicket || !data?.message) {
          console.info("[Notifications] Evento ignorado: payload sem ticket ou message.", {
            hasTicket: Boolean(payloadTicket),
            hasMessage: Boolean(data?.message),
            action: data?.action,
          });
          return;
        }

        const currentUserId = toNumberOrNull(user?.id);
        const messageUserId = toNumberOrNull(data?.message?.userId);
        const senderUserId = toNumberOrNull(data?.message?.senderId);
        const ticketUserId = toNumberOrNull(payloadTicket?.userId);
        const ticketQueueId = toNumberOrNull(payloadTicket?.queueId);
        const fromMe = normalizeFromMe(data.message.fromMe);
        const isOwnedByCurrentUser =
          (messageUserId && messageUserId === currentUserId) ||
          (senderUserId && senderUserId === currentUserId);
        const isIncomingLike = !fromMe && !isOwnedByCurrentUser;
        let pendingBeepPlayed = false;
        const belongsToUser =
          (ticketUserId && ticketUserId === currentUserId) || !ticketUserId;
        const belongsToQueue =
          user?.queues?.some(
            (queue) => toNumberOrNull(queue?.id) === ticketQueueId
          ) ||
          (!ticketQueueId &&
            (showTicketWithoutQueue === true ||
              ["pending", "open"].includes(payloadTicket?.status)));
        const validStatus = ["pending", "open"].includes(payloadTicket?.status);
        const allowedStatus = !["lgpd", "nps"].includes(payloadTicket?.status);
        const groupBlocked =
          payloadTicket.isGroup &&
          (!showGroupNotification ||
            payloadTicket?.whatsapp?.groupAsTicket === "disabled");

        if (
          data.action === "create" &&
          validStatus &&
          isIncomingLike &&
          belongsToUser &&
          belongsToQueue &&
          allowedStatus &&
          showNotificationPending === true
        ) {
          if (groupBlocked) {
            console.info("[Notifications] Notificacao bloqueada para grupo.", {
              ticketId: payloadTicket?.id,
              showGroupNotification,
              groupAsTicket: payloadTicket?.whatsapp?.groupAsTicket,
            });
            return;
          }

          setTicketNotifications((prevState) => {
            const ticketIndex = prevState.findIndex(
              (t) => t.id === payloadTicket.id
            );
            if (ticketIndex !== -1) {
              const updatedTickets = [...prevState];
              updatedTickets[ticketIndex] = payloadTicket;
              return updatedTickets;
            }
            return [payloadTicket, ...prevState];
          });

          const isSameVisibleTicket = isTicketCurrentlyVisible(
            payloadTicket,
            data?.message?.ticketId
          );

          const shouldNotNotificate =
            !isPendingTicketNotification(payloadTicket) ||
            isSameVisibleTicket ||
            (ticketUserId && ticketUserId !== currentUserId) ||
            (payloadTicket.isGroup &&
              (payloadTicket?.whatsapp?.groupAsTicket === "disabled" ||
                showGroupNotification === false));

          if (shouldNotNotificate !== true && handleNotificationsRef.current) {
            handleNotificationsRef.current({
              ...data,
              ticket: payloadTicket,
            });
            pendingBeepPlayed = true;
          } else {
            console.info("[Notifications] Notificacao suprimida por contexto da tela.", {
              ticketId: payloadTicket?.id,
              currentTicketId: currentTicketPathRef.current,
              messageTicketId: data?.message?.ticketId,
              visibilityState: document.visibilityState,
              assignedUserId: ticketUserId,
              currentUserId,
              isGroup: payloadTicket?.isGroup,
              groupAsTicket: payloadTicket?.whatsapp?.groupAsTicket,
              showGroupNotification,
            });
          }
        } else if (data.action === "create") {
          console.info("[Notifications] Notificacao nao disparada por regra de negocio.", {
            ticketId: payloadTicket?.id,
            messageId: data?.message?.id,
            validStatus,
            status: payloadTicket?.status,
            isIncomingLike,
            fromMe,
            isOwnedByCurrentUser,
            belongsToUser,
            belongsToQueue,
            queueId: ticketQueueId,
            userQueueIds: Array.isArray(user?.queues)
              ? user.queues.map((queue) => toNumberOrNull(queue?.id))
              : [],
            allowedStatus,
            showNotificationPending,
            isGroup: payloadTicket?.isGroup,
            groupBlocked,
          });
        }

        if (
          data.action === "create" &&
          isIncomingLike &&
          isPendingTicketNotification(payloadTicket) &&
          !payloadTicket?.isGroup &&
          !["lgpd", "nps"].includes(payloadTicket?.status) &&
          ((ticketUserId && ticketUserId === currentUserId) || !ticketUserId) &&
          (user?.queues?.some(
            (queue) => toNumberOrNull(queue?.id) === ticketQueueId
          ) ||
            (!ticketQueueId && showTicketWithoutQueue === true))
        ) {
          const isSameVisibleTicket = isTicketCurrentlyVisible(
            payloadTicket,
            data?.message?.ticketId
          );

          if (!isSameVisibleTicket && !pendingBeepPlayed) {
            if (soundAlertRef.current) {
              soundAlertRef.current(payloadTicket?.contact?.notificationSound);
            }
          }
        }
      };

      const onCompanyTodoNotificationsPopover = (data) => {
        const notificationData = data?.notification;

        if (Number(notificationData?.userId) !== Number(user.id)) {
          return;
        }

        if (data.action === "delete") {
          setTodoNotifications((prevState) =>
            prevState.filter((item) => item.id !== notificationData.id)
          );
          return;
        }

        setTodoNotifications((prevState) => {
          const currentIndex = prevState.findIndex(
            (item) => item.id === notificationData.id
          );
          if (currentIndex !== -1) {
            const updated = [...prevState];
            updated[currentIndex] = notificationData;
            return updated;
          }
          return [notificationData, ...prevState];
        });

        if (document.visibilityState !== "visible") {
          handleTodoDesktopNotification(notificationData);
        } else if (soundAlertRef.current) {
          soundAlertRef.current();
        }
      };

      const onCompanySupportNotificationsPopover = (data) => {
        const notificationData = data?.notification;

        if (data?.action !== "notification" || !notificationData) {
          return;
        }

        if (Number(notificationData?.userId) !== Number(user.id)) {
          return;
        }

        setSupportNotifications((prevState) => {
          const currentIndex = prevState.findIndex(
            (item) => item.id === notificationData.id
          );

          if (currentIndex !== -1) {
            const updated = [...prevState];
            updated[currentIndex] = notificationData;
            return updated;
          }

          return [notificationData, ...prevState];
        });

        if (document.visibilityState !== "visible") {
          handleSupportDesktopNotification(notificationData);
        } else if (soundAlertRef.current) {
          soundAlertRef.current();
        }
      };

      const onCompanyScheduleReminderNotificationsPopover = (data) => {
        const notificationData = data?.notification;

        if (data?.action !== "trigger" || !notificationData) {
          return;
        }

        if (
          notificationData?.userId &&
          Number(notificationData.userId) !== Number(user.id)
        ) {
          return;
        }

        handleScheduleReminderNotification(notificationData);
      };

      const onCompanyChatNotificationsPopover = (data) => {
        if (data?.action === "new-message") {
          const isFromMe = Number(data?.newMessage?.senderId) === Number(user?.id);
          const isParticipant = data?.chat?.users?.some(
            (u) => Number(u.userId) === Number(user?.id)
          );

          if (!isParticipant) return;

          if (!isFromMe) {
            const currentPath = historyRef.current?.location?.pathname || "";
            const isInsideThisChat =
              currentPath === `/chats/${data.chat?.uuid}` ||
              currentPath === `/chats/${data.chat?.id}`;

            if (!isInsideThisChat) {
              if (typeof soundAlertRef.current === "function") {
                soundAlertRef.current();
              }

              if (canUseDesktopNotifications() && document.visibilityState !== "visible") {
                try {
                  const plainText = String(data.newMessage?.message || "")
                    .replace(/<[^>]*>?/gm, "")
                    .trim();
                  new Notification(`Chat: ${data.newMessage?.sender?.name || "Nova mensagem"}`, {
                    body: plainText || "Nova mensagem no chat interno",
                    tag: `chat-${data.chat?.id}`,
                    renotify: true,
                  });
                } catch (_) {}
              }
            }
          }

          setChatNotifications((prev) => {
            const userChat = data.chat?.users?.find(
              (u) => Number(u.userId) === Number(user?.id)
            );
            const unreads = Number(userChat?.unreads || 0);

            const currentPath = historyRef.current?.location?.pathname || "";
            const isInsideThisChat =
              currentPath === `/chats/${data.chat?.uuid}` ||
              currentPath === `/chats/${data.chat?.id}`;

            if (unreads === 0 || isInsideThisChat) {
              return prev.filter((c) => c.id !== data.chat.id);
            }

            const existingIndex = prev.findIndex((c) => c.id === data.chat.id);
            const chatItem = {
              ...data.chat,
              type: "internalChat",
              lastMessage: data.newMessage,
            };

            if (existingIndex !== -1) {
              const updated = [...prev];
              updated[existingIndex] = chatItem;
              return updated;
            }
            return [chatItem, ...prev];
          });
        }

        if (data?.action === "update") {
          setChatNotifications((prev) => {
            const userChat = data.chat?.users?.find(
              (u) => Number(u.userId) === Number(user?.id)
            );
            const unreads = Number(userChat?.unreads || 0);

            if (unreads === 0) {
              return prev.filter((c) => c.id !== data.chat.id);
            }

            const existingIndex = prev.findIndex((c) => c.id === data.chat.id);
            const chatItem = {
              ...data.chat,
              type: "internalChat",
            };

            if (existingIndex !== -1) {
              const updated = [...prev];
              updated[existingIndex] = chatItem;
              return updated;
            }
            return [chatItem, ...prev];
          });
        }
      };

      socket.on("connect", onConnectNotificationsPopover);
      if (socket.connected) {
        onConnectNotificationsPopover();
      }
      socket.on(
        `company-${companyId}-ticket`,
        onCompanyTicketNotificationsPopover
      );
      socket.on(
        `company-${companyId}-appMessage`,
        onCompanyAppMessageNotificationsPopover
      );
      socket.on(
        `company-${companyId}-todoTask`,
        onCompanyTodoNotificationsPopover
      );
      socket.on(
        `company-${companyId}-supportTicket`,
        onCompanySupportNotificationsPopover
      );
      socket.on(
        `company-${companyId}-scheduleNotification`,
        onCompanyScheduleReminderNotificationsPopover
      );
      socket.on(
        `company-${companyId}-chat`,
        onCompanyChatNotificationsPopover
      );

      return () => {
        socket.off("connect", onConnectNotificationsPopover);
        socket.off(
          `company-${companyId}-ticket`,
          onCompanyTicketNotificationsPopover
        );
        socket.off(
          `company-${companyId}-appMessage`,
          onCompanyAppMessageNotificationsPopover
        );
        socket.off(
          `company-${companyId}-todoTask`,
          onCompanyTodoNotificationsPopover
        );
        socket.off(
          `company-${companyId}-supportTicket`,
          onCompanySupportNotificationsPopover
        );
        socket.off(
          `company-${companyId}-scheduleNotification`,
          onCompanyScheduleReminderNotificationsPopover
        );
        socket.off(
          `company-${companyId}-chat`,
          onCompanyChatNotificationsPopover
        );
      };
    }
  }, [
    user,
    profile,
    queues,
    showTicketWithoutQueue,
    socket,
    showNotificationPending,
    showGroupNotification,
    handleTodoDesktopNotification,
    handleSupportDesktopNotification,
    handleScheduleReminderNotification,
    isTicketCurrentlyVisible,
  ]);

  const handleClick = useCallback(() => {
    setIsOpen((prevState) => !prevState);
  }, []);

  const handleClickAway = useCallback(() => {
    if (!modalOpen) {
      setIsOpen(false);
    }
  }, [modalOpen]);

  const handleNotificationClick = useCallback(
    (ticket) => {
      openTicketFromNotification(ticket);
    },
    [openTicketFromNotification]
  );

  const NotificatiOnticket = useCallback(
    ({ children, ticket }) => {
      return (
        <div
          className={classes.notificatiOnticket}
          onClick={() => handleNotificationClick(ticket)}
        >
          <div className={classes.ticketItemContainer}>{children}</div>
        </div>
      );
    },
    [
      handleNotificationClick,
      classes.notificatiOnticket,
      classes.ticketItemContainer,
    ]
  );

  const notifications = useMemo(
    () => [
      ...chatNotifications,
      ...supportNotifications,
      ...todoNotifications,
      ...ticketNotifications,
    ],
    [
      chatNotifications,
      supportNotifications,
      todoNotifications,
      ticketNotifications,
    ]
  );

  const filteredNotifications = useMemo(() => {
    if (activeFilter === "todo") {
      return notifications.filter((item) => item.type === "todoTask");
    }

    if (activeFilter === "ticket") {
      return notifications.filter(
        (item) =>
          item.type !== "todoTask" &&
          item.type !== "internalChat" &&
          item.type !== "supportTicket"
      );
    }

    if (activeFilter === "chat") {
      return notifications.filter((item) => item.type === "internalChat");
    }

    return notifications;
  }, [activeFilter, notifications]);

  const notificationCounters = useMemo(
    () => ({
      all: notifications.length,
      chat: notifications.filter((item) => item.type === "internalChat").length,
      todo: notifications.filter((item) => item.type === "todoTask").length,
      ticket: notifications.filter(
        (item) =>
          item.type !== "todoTask" &&
          item.type !== "internalChat" &&
          item.type !== "supportTicket"
      ).length,
    }),
    [notifications]
  );

  const browserNotification = useMemo(() => {
    const numbers = "⓿➊➋➌➍➎➏➐➑➒➓⓫⓬⓭⓮⓯⓰⓱⓲⓳⓴";
    if (notifications.length > 0) {
      if (notifications.length < 21) {
        document.title =
          numbers.substring(notifications.length, notifications.length + 1) +
          " - " +
          (theme.appName || "...");
      } else {
        document.title =
          "(" + notifications.length + ")" + (theme.appName || "...");
      }
    } else {
      document.title = theme.appName || "...";
    }
    return (
      <>
        <Favicon
          animated={true}
          url={
            theme?.appLogoFavicon ? theme.appLogoFavicon : defaultLogoFavicon
          }
          alertCount={notifications.length}
          iconSize={195}
        />
      </>
    );
  }, [notifications.length, theme.appName, theme.appLogoFavicon]);

  const unreadNotifications = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const badgeProps = useMemo(
    () => ({
      color: "secondary",
      variant: "dot",
      invisible: unreadNotifications === 0,
    }),
    [unreadNotifications]
  );

  const renderNotifications = useMemo(() => {
    if (filteredNotifications.length === 0) {
      return (
        <ListItem>
          <ListItemText>
            <Typography className={classes.emptyList}>
              {i18n.t("notifications.noTickets") ||
                "Nenhuma notificação disponível"}
            </Typography>
          </ListItemText>
        </ListItem>
      );
    }

    return filteredNotifications.map((item) => {
      if (item.type === "todoTask") {
        return (
          <ListItem
            button
            key={item.id}
            className={`${classes.todoNotificationItem} ${
              !item.read ? classes.todoNotificationUnread : ""
            }`}
            onClick={() => openTodoFromNotification(item)}
          >
            <ListItemAvatar>
              <Avatar className={classes.todoAvatar}>T</Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <div className={classes.todoPrimary}>
                  <span className={classes.todoTitle}>{item.title}</span>
                  {!item.read ? (
                    <Chip
                      size="small"
                      label="Nova"
                      className={classes.unreadBadge}
                    />
                  ) : null}
                </div>
              }
              secondary={[
                item.description || "Tarefa atualizada",
                item.startDate || item.endDate
                  ? `Período: ${item.startDate || "-"} até ${item.endDate || "-"}`
                  : "",
                `Atualizado por ${item.actorName}`,
                item.createdAt
                  ? `Recebido às ${format(new Date(item.createdAt), "HH:mm")}`
                  : "",
              ]
                .filter(Boolean)
                .join(" | ")}
              secondaryTypographyProps={{ className: classes.todoMeta }}
            />
          </ListItem>
        );
      }

      if (item.type === "internalChat") {
        const otherUser = Array.isArray(item.users)
          ? item.users.find((u) => Number(u.userId) !== Number(user?.id))?.user
          : null;
        const chatTitle = item.isGroup
          ? item.title || "Grupo"
          : otherUser?.name || "Chat Interno";
        const userChat = item.users?.find(
          (u) => Number(u.userId) === Number(user?.id)
        );
        const unreads = userChat?.unreads || 1;
        const lastMsg =
          item.lastMessage?.message ||
          item.lastMessage ||
          "Nova mensagem recebida";

        return (
          <ListItem
            button
            key={`chat-${item.id}`}
            className={`${classes.todoNotificationItem} ${classes.todoNotificationUnread}`}
            onClick={() => openChatFromNotification(item)}
          >
            <ListItemAvatar>
              <Avatar
                className={classes.todoAvatar}
                style={{ backgroundColor: "#4f46e5" }}
              >
                {item.isGroup
                  ? "G"
                  : (chatTitle.charAt(0) || "C").toUpperCase()}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <div className={classes.todoPrimary}>
                  <span className={classes.todoTitle}>{chatTitle}</span>
                  <Chip
                    size="small"
                    label={unreads > 1 ? `${unreads} novas` : "Nova"}
                    className={classes.unreadBadge}
                  />
                </div>
              }
              secondary={[
                typeof lastMsg === "string" ? lastMsg : "Nova mensagem",
                item.updatedAt
                  ? `Recebido às ${format(new Date(item.updatedAt), "HH:mm")}`
                  : "",
              ]
                .filter(Boolean)
                .join(" | ")}
              secondaryTypographyProps={{ className: classes.todoMeta }}
            />
          </ListItem>
        );
      }

      if (item.type === "supportTicket") {
        return (
          <ListItem
            button
            key={`support-${item.id}`}
            className={`${classes.todoNotificationItem} ${
              !item.read ? classes.todoNotificationUnread : ""
            }`}
            onClick={() => openSupportFromNotification(item)}
          >
            <ListItemAvatar>
              <Avatar className={classes.todoAvatar}>S</Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <div className={classes.todoPrimary}>
                  <span className={classes.todoTitle}>{item.title}</span>
                  {!item.read ? (
                    <Chip
                      size="small"
                      label="Nova"
                      className={classes.unreadBadge}
                    />
                  ) : null}
                </div>
              }
              secondary={[
                item.message || "Você recebeu uma atualização de suporte.",
                item.subject ? `Assunto: ${item.subject}` : "",
                item.companyName ? `Empresa: ${item.companyName}` : "",
                item.createdAt
                  ? `Recebido às ${format(new Date(item.createdAt), "HH:mm")}`
                  : "",
              ]
                .filter(Boolean)
                .join(" | ")}
              secondaryTypographyProps={{ className: classes.todoMeta }}
            />
            {item.ticketId ? (
              <Link
                component="button"
                variant="caption"
                onClick={(event) => {
                  event.stopPropagation();
                  openSupportFromNotification(item);
                }}
              >
                Ver
              </Link>
            ) : null}
          </ListItem>
        );
      }

      return (
        <NotificatiOnticket key={item.id} ticket={item}>
          <TicketListItem
            ticket={item}
            setTabOpen={setTabOpen}
            setModalOpen={setModalOpen}
            hideLastMessage
          />
        </NotificatiOnticket>
      );
    });
  }, [
    filteredNotifications,
    classes.emptyList,
    classes.todoAvatar,
    classes.todoMeta,
    classes.todoNotificationItem,
    classes.todoNotificationUnread,
    classes.todoPrimary,
    classes.todoTitle,
    classes.unreadBadge,
    setTabOpen,
    setModalOpen,
    openTodoFromNotification,
    openSupportFromNotification,
    openChatFromNotification,
    user?.id,
  ]);

  return (
    <>
      {browserNotification}

      <StandardModal
        open={Boolean(scheduleReminder)}
        onClose={() => setScheduleReminder(null)}
        title={scheduleReminder?.title || "Agendamento"}
        maxWidth="sm"
        showFooter
        primaryButton={{
          text: "Ver agendamentos",
          color: "primary",
          onClick: () => {
            setScheduleReminder(null);
            history.push("/schedules");
          },
        }}
        secondaryButton={{
          text: "Fechar",
          color: "secondary",
          onClick: () => setScheduleReminder(null),
        }}
      >
        <Typography variant="body1" gutterBottom>
          {scheduleReminder?.message || "Você possui um agendamento interno pendente."}
        </Typography>
        {scheduleReminder?.sendAt ? (
          <Typography variant="body2" color="textSecondary">
            Horário: {format(new Date(scheduleReminder.sendAt), "dd/MM/yyyy HH:mm")}
          </Typography>
        ) : null}
      </StandardModal>

      <div ref={anchorEl}>
        <IconButtonWithTooltip
          icon={Icon}
          tooltip={`${i18n.t("notifications.title")}${
            desktopNotifications.length > 0
              ? ` (${desktopNotifications.length})`
              : ""
          }`}
          onClick={handleClick}
          aria-label={i18n.t("notifications.title")}
          badgeProps={badgeProps}
        />
      </div>
      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
        onClose={handleClickAway}
      >
        <div
          className={classes.popoverHeader}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle2">
            {i18n.t("notifications.title") || "Notificações Pendentes"}
          </Typography>
        </div>
        <div className={classes.filterRow}>
          <Button
            size="small"
            variant={activeFilter === "all" ? "contained" : "text"}
            color="primary"
            className={`${classes.filterButton} ${
              activeFilter === "all" ? classes.filterButtonActive : ""
            }`}
            onClick={() => setActiveFilter("all")}
          >
            Todas ({notificationCounters.all})
          </Button>
          <Button
            size="small"
            variant={activeFilter === "chat" ? "contained" : "text"}
            color="primary"
            className={`${classes.filterButton} ${
              activeFilter === "chat" ? classes.filterButtonActive : ""
            }`}
            onClick={() => setActiveFilter("chat")}
          >
            Chat ({notificationCounters.chat})
          </Button>
          <Button
            size="small"
            variant={activeFilter === "ticket" ? "contained" : "text"}
            color="primary"
            className={`${classes.filterButton} ${
              activeFilter === "ticket" ? classes.filterButtonActive : ""
            }`}
            onClick={() => setActiveFilter("ticket")}
          >
            Tickets ({notificationCounters.ticket})
          </Button>
          <Button
            size="small"
            variant={activeFilter === "todo" ? "contained" : "text"}
            color="primary"
            className={`${classes.filterButton} ${
              activeFilter === "todo" ? classes.filterButtonActive : ""
            }`}
            onClick={() => setActiveFilter("todo")}
          >
            Tarefas ({notificationCounters.todo})
          </Button>
        </div>
        <div className={classes.notificationContainer}>
          <List dense disablePadding className={classes.tabContainer}>
            {renderNotifications}
          </List>
        </div>
      </Popover>
    </>
  );
});

export default NotificationsPopOver;
